'use client'
import type { ColumnDefWithSkeleton } from '@morpho-org/ui-v6/types'

import { CellTokenAmountPrice } from '@morpho-org/ui-v6/client'
import { CellAllocation, Skeleton, V1Badge } from '@morpho-org/ui-v6/server'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import { rowSortingFeature, tableFeatures } from '@tanstack/react-table'

import { ButtonLink } from '@/components/common/ButtonLink'
import { UserCellDashboard } from '@/components/common/Cells/UserCell'

import type { MarketSuppliersTableRow } from './MarketSuppliers.types'

// Keeps enableSorting: false effective; without rowSortingFeature, accessor headers render as sortable.
export const marketSuppliersTableFeatures = tableFeatures({
  rowSortingFeature
})
export type MarketSuppliersTableFeatures = typeof marketSuppliersTableFeatures

export const marketSuppliersColumnsDefs: ColumnDefWithSkeleton<
  MarketSuppliersTableFeatures,
  MarketSuppliersTableRow
>[] = [
  {
    id: 'supplier',
    accessorKey: 'supplier',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground" style={{ marginLeft: '14px' }}>
            Supplier
          </span>
        </TooltipTrigger>
        <TooltipContent>The account supplying the market.</TooltipContent>
      </Tooltip>
    ),
    cell: info => {
      const { vault } = info.row.original.supplier
      const isVaultV2 = info.row.original.isVaultV2

      if (vault) {
        return (
          <ButtonLink
            href={info.row.original.supplier.url}
            mode="internal"
            buttonProps={{
              variant: 'ghost',
              size: 'default',
              label: (
                <div className="gap-xs flex items-center" style={{ minWidth: 0 }}>
                  <span className="text-2xs text-foreground line-clamp-1">{vault.name}</span>
                  {!isVaultV2 && <V1Badge tabIndex={-1} />}
                </div>
              ),
              avatarProps: {
                src: vault.logoUri ?? undefined
              }
            }}
          />
        )
      }

      return <UserCellDashboard address={info.row.original.supplier.address} />
    },

    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[144px] rounded-[20px]" />
  },
  {
    id: 'totalSupply',
    accessorKey: 'totalSupply',
    header: info => {
      const table = info.table
      const firstRow = table.getRowModel().rows[0]
      const totalSupply = firstRow?.original.totalSupply

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-3xs text-secondary-foreground">
              Supply{totalSupply ? ` (${totalSupply.asset?.symbol})` : ''}
            </span>
          </TooltipTrigger>
          <TooltipContent>The total amount of loan assets supplied to the market.</TooltipContent>
        </Tooltip>
      )
    },
    cell: info => (
      <CellTokenAmountPrice
        token={info.row.original.totalSupply.asset}
        amount={info.row.original.totalSupply.amount}
        value={info.row.original.totalSupply.amountUsd}
        withAvatar
        omitSymbol
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[195px] rounded-[20px]" />
  },
  {
    id: 'share',
    accessorKey: 'share',
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">Supply Share</span>
        </TooltipTrigger>
        <TooltipContent>
          The proportion of the total supply allocated to this market.
        </TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellAllocation
        allocation={row.original.supplyShare.value}
        index={row.index}
        percentageOffset={row.original.supplyShare.percentageOffset}
      />
    ),
    enableSorting: false,
    skeletonCell: <Skeleton className="h-[22px] w-[81px] rounded-[20px]" />
  }
]
